import { FunctionComponent, useCallback, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminSideMenu from "../components/AdminSideMenu";
import AdminTopMenu from "../components/AdminTopMenu/AdminTopMenu"; // Added import
import { useRooms } from "../contexts/RoomContext";
import styles from "./AdvancedSetupScreen.module.css";

const AdvancedSetupScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const roomId = id ? parseInt(id) : undefined;
  const { currentRoom, loading, error, fetchRoomById, editRoom } = useRooms();

  const [formData, setFormData] = useState({
    phrases: "",
    blockedWords: "",
    sttModel: "Google",
    translationModel: "Google",
    ttsModel: "Google",
    ttsSpeedRate: 1, // Added TTS speed rate with default value
    phraseToReplace: "", // Added for the new phrase replacement input
    replacementWord: "",  // Added for the new replacement word input
    roomImageUrl: "", // Added for room image URL
    wordsReplacements: {} as Record<string, string>, // Added for word replacements
    isTest: false, // Mark if this is a test room
    domain: 'general' as 'general' | 'medical' | 'legal' | 'technical' | 'islamic' // Added domain selection
  });

  const [imageUploadLoading, setImageUploadLoading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);

  // Navigate to Join Info screen, passing room ID if available
  const onJoinInfoClick = useCallback(() => {
    navigate(roomId ? `/join-info/${roomId}` : "/join-info");
  }, [navigate, roomId]);

  // Navigate to Basic Setup screen
  const onBasicSetupClick = useCallback(() => {
    navigate(roomId ? `/basic-setup/${roomId}` : "/basic-setup");
  }, [navigate, roomId]);

  // Navigate back to room list
  const onFrameClick2 = useCallback(() => {
    navigate("/rooms"); // Changed from /my-room-screen
  }, [navigate]);

  const onCancelClick = useCallback(() => { // Added for Cancel button
    navigate("/rooms");
  }, [navigate]);

  // Fetch room data if editing an existing room
  useEffect(() => {
    if (roomId) {
      fetchRoomById(roomId);
    }
  }, [roomId, fetchRoomById]);

  // Update form when currentRoom changes
  useEffect(() => {
    if (currentRoom && roomId) {
      // Parse wordsReplacements from JSON string if it exists
      let wordsReplacements = {};
      try {
        if ((currentRoom as any).wordsReplacements) {
          wordsReplacements = typeof (currentRoom as any).wordsReplacements === 'string'
            ? JSON.parse((currentRoom as any).wordsReplacements)
            : (currentRoom as any).wordsReplacements;
        }
      } catch (e) {
        console.error("Failed to parse wordsReplacements:", e);
        wordsReplacements = {};
      }

      setFormData({
        phrases: currentRoom.phrases || "",
        blockedWords: currentRoom.blockWords || "", // Ensure this matches the backend field name
        sttModel: currentRoom.sttModel || "Google",
        translationModel: currentRoom.translationModel || "Google",
        ttsModel: currentRoom.ttsModel || "Google",
        ttsSpeedRate: (currentRoom as any).ttsSpeedRate || 0.9, // Initialize TTS speed rate
        phraseToReplace: "", // Initialize or load from currentRoom if applicable
        replacementWord: "",  // Initialize or load from currentRoom if applicable
        roomImageUrl: (currentRoom as any).imageUrl || "", // Initialize room image URL from imageUrl field
        wordsReplacements: wordsReplacements,
        isTest: (currentRoom as any).isTest ?? false,
        domain: (currentRoom as any).domain || 'general' // Initialize domain from currentRoom
      });
    }
  }, [currentRoom, roomId]);

  // Handle input changes
  const handleInputChange = (field: string, value: string | number | boolean) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  // Handle save button click
  const handleSaveSettings = async () => {
    if (!roomId) return;

    try {
      await editRoom(roomId, {
        phrases: formData.phrases,
        blockWords: formData.blockedWords, // Ensure this matches the backend field name
        sttModel: formData.sttModel,
        translationModel: formData.translationModel,
        ttsModel: formData.ttsModel,
        ttsSpeedRate: formData.ttsSpeedRate, // Include TTS speed rate
        imageUrl: formData.roomImageUrl, // Use imageUrl as expected by API
        wordsReplacements: JSON.stringify(formData.wordsReplacements), // Stringify word replacements before sending to API
        isTest: formData.isTest,
        domain: formData.domain // Include domain in the payload
      } as any);

      // Navigate to the rooms screen after saving
      navigate("/rooms"); // Changed from onJoinInfoClick()
    } catch (error) {
      console.error("Failed to save advanced settings:", error);
      // Display error to user
    }
  };

  // Handle adding phrase replacement
  const handleAddPhraseReplacement = () => {
    if (!formData.phraseToReplace.trim() || !formData.replacementWord.trim()) {
      console.log("Both phrase and replacement must be provided");
      return;
    }

    // Add the new replacement to the wordsReplacements object
    const updatedReplacements = {
      ...formData.wordsReplacements,
      [formData.phraseToReplace.trim()]: formData.replacementWord.trim()
    };

    setFormData(prev => ({
      ...prev,
      wordsReplacements: updatedReplacements,
      phraseToReplace: '',
      replacementWord: ''
    }));
  };

  // Handle removing a phrase replacement
  const handleRemovePhraseReplacement = (phraseToRemove: string) => {
    const updatedReplacements = { ...formData.wordsReplacements };
    delete updatedReplacements[phraseToRemove];

    setFormData(prev => ({
      ...prev,
      wordsReplacements: updatedReplacements
    }));
  };

  // Function to resize image if width > 1600px
  const resizeImage = (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        const maxWidth = 1600;
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;
        ctx!.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          const resizedFile = new File([blob!], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        }, file.type, 0.9);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // Function to upload room image
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset error state
    setImageUploadError(null);

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setImageUploadError('Please select a valid image file (JPG, JPEG, or PNG)');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setImageUploadError('Image size must be less than 5MB');
      return;
    }

    try {
      setImageUploadLoading(true);

      // Resize image if necessary
      const processedFile = await resizeImage(file);

      // Create form data for upload
      const formDataUpload = new FormData();
      formDataUpload.append('image', processedFile);

      // Get token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Upload image
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const uploadUrl = apiBaseUrl ? `${apiBaseUrl}/upload/room-image` : '/api/upload/room-image';

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const result = await response.json();

      if (result.status === 'success' && result.data?.imageUrl) {
        // Update form data with new image URL
        setFormData(prev => ({
          ...prev,
          roomImageUrl: result.data.imageUrl
        }));
      } else {
        throw new Error('Invalid response from server');
      }

    } catch (error) {
      console.error('Image upload error:', error);
      setImageUploadError(error instanceof Error ? error.message : 'Failed to upload image');
    } finally {
      setImageUploadLoading(false);
      // Clear the input
      event.target.value = '';
    }
  };

  // Function to remove uploaded image
  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      roomImageUrl: ''
    }));
    setImageUploadError(null);
  };


  return (
    <div>
      <AdminSideMenu />
      <AdminTopMenu />
      <div className={styles.advancedSetupScreenWrapper}> {/* Outermost wrapper */}
        <div className={styles.mainContentContainer}> {/* Main content area */}
          <div className={styles.tabsContainer}>
            <div className={styles.tabItem} onClick={onBasicSetupClick}>Basic setup</div>
            <div className={`${styles.tabItem} ${styles.activeTab}`}>Advanced settings</div>
            <div className={styles.tabItem} onClick={onJoinInfoClick}>{`Join Info`}</div>
          </div>

          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading room data...</p>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <p>Error: {error}</p>
              {/* Consider adding a back button here if appropriate */}
            </div>
          ) : (
            <div className={styles.formContainer}>
              {/* Most Used Phrases */}
              <div className={styles.formGroup}>
                <label htmlFor="phrases">Most Used Phrases</label>
                <textarea
                  id="phrases"
                  value={formData.phrases}
                  onChange={(e) => handleInputChange("phrases", e.target.value)}
                  placeholder="Add phrases separated by semicolons (;)"
                />
              </div>

              {/* Blocked Words/Phrases */}
              <div className={styles.formGroup}>
                <label htmlFor="blockedWords">
                  Blocked Words/Phrases
                  <img
                    className={styles.infoButtonIcon}
                    alt="Info"
                    src="/info-button@2x.png"
                    title="Words/phrases listed here will be blocked."
                  />
                </label>
                <textarea
                  id="blockedWords"
                  value={formData.blockedWords}
                  onChange={(e) => handleInputChange("blockedWords", e.target.value)}
                  placeholder="Add words to block separated by semicolons (;)"
                />
              </div>

              {/* Speech to Text Model */}
              <div className={styles.formGroup}>
                <label htmlFor="sttModel">
                  Speech to Text Model
                  <img
                    className={styles.infoButtonIcon}
                    alt="Info"
                    src="/info-button@2x.png"
                    title="Select the STT model."
                  />
                </label>
                <div className={styles.selectContainer}>
                  <select
                    id="sttModel"
                    value={formData.sttModel}
                    onChange={(e) => handleInputChange("sttModel", e.target.value)}
                  >
                    <option value="Google">Google</option>
                  </select>
                </div>
              </div>

              {/* Translation Model */}
              <div className={styles.formGroup}>
                <label htmlFor="translationModel">
                  Translation Model
                  <img
                    className={styles.infoButtonIcon}
                    alt="Info"
                    src="/info-button@2x.png"
                    title="Select the translation model."
                  />
                </label>
                <div className={styles.selectContainer}>
                  <select
                    id="translationModel"
                    value={formData.translationModel}
                    onChange={(e) => handleInputChange("translationModel", e.target.value)}
                  >
                    <option value="Google">Google</option>
                  </select>
                </div>
              </div>

              {/* Text to Speech Model */}
              <div className={styles.formGroup}>
                <label htmlFor="ttsModel">
                  Text to Speech Model
                  <img
                    className={styles.infoButtonIcon}
                    alt="Info"
                    src="/info-button@2x.png"
                    title="Select the TTS model."
                  />
                </label>
                <div className={styles.selectContainer}>
                  <select
                    id="ttsModel"
                    value={formData.ttsModel}
                    onChange={(e) => handleInputChange("ttsModel", e.target.value)}
                  >
                    <option value="Google">Google</option>
                  </select>
                </div>
              </div>

              {/* TTS Speed Rate */}
              <div className={styles.formGroup}>
                <label htmlFor="ttsSpeedRate">
                  TTS Speed Rate
                  <img
                    className={styles.infoButtonIcon}
                    alt="Info"
                    src="/info-button@2x.png"
                    title="Adjust the speed of text-to-speech playback (0.5 = slow, 2.0 = fast)."
                  />
                </label>
                <div className={styles.numberInputContainer}>
                  <input
                    type="number"
                    id="ttsSpeedRate"
                    value={formData.ttsSpeedRate}
                    onChange={(e) => handleInputChange("ttsSpeedRate", parseFloat(e.target.value))}
                    min="0.5"
                    max="2.0"
                    step="0.1"
                    className={styles.numberInput}
                  />
                  <span className={styles.speedRateLabel}>
                    {formData.ttsSpeedRate < 1 ? 'Slow' : formData.ttsSpeedRate === 1 ? 'Normal' : 'Fast'}
                  </span>
                </div>
              </div>

              {/* Room Image Upload Section */}
              <div className={styles.formGroup}>
                <label htmlFor="roomImage">
                  Room Image
                  <img
                    className={styles.infoButtonIcon}
                    alt="Info"
                    src="/info-button@2x.png"
                    title="Upload a room image (JPG, JPEG, PNG - max 5MB). Images wider than 1600px will be resized."
                  />
                </label>
                <div className={styles.imageUploadContainer}>
                  {formData.roomImageUrl ? (
                    <div className={styles.uploadedImageContainer}>
                      <img
                        src={formData.roomImageUrl}
                        alt="Room"
                        className={styles.uploadedImage}
                      />
                      <button
                        type="button"
                        className={styles.removeImageButton}
                        onClick={handleRemoveImage}
                        title="Remove image"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className={styles.imageUploadDropzone}>
                      <input
                        type="file"
                        id="roomImage"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleImageUpload}
                        disabled={imageUploadLoading}
                        className={styles.imageUploadInput}
                      />
                      <label htmlFor="roomImage" className={styles.imageUploadLabel}>
                        {imageUploadLoading ? (
                          <div className={styles.uploadingText}>Uploading...</div>
                        ) : (
                          <>
                            <div className={styles.uploadIcon}>📁</div>
                            <div className={styles.uploadText}>
                              Click to upload room image
                            </div>
                            <div className={styles.uploadHint}>
                              JPG, JPEG, PNG (max 5MB)
                            </div>
                          </>
                        )}
                      </label>
                    </div>
                  )}
                  {imageUploadError && (
                    <div className={styles.imageUploadError}>
                      {imageUploadError}
                    </div>
                  )}
                </div>
              </div>

              {/* Phrase Replacement Section */}
              <div className={styles.phraseReplacementSection}>
                <h2 className={styles.phraseReplacementTitle}>
                  Optimize Translated Text
                  <img
                    className={styles.infoButtonIcon}
                    alt="Info"
                    src="/info-button@2x.png"
                    title="Enforce consistent word replacements."
                  />
                </h2>

                {/* Current Word Replacements List */}
                {Object.keys(formData.wordsReplacements).length > 0 && (
                  <div className={styles.currentReplacements}>
                    <h3 className={styles.currentReplacementsTitle}>Current Word Replacements:</h3>
                    <div className={styles.replacementsList}>
                      {Object.entries(formData.wordsReplacements).map(([original, replacement]) => (
                        <div key={original} className={styles.replacementItem}>
                          <span className={styles.originalPhrase}>"{original}"</span>
                          <span className={styles.arrow}>→</span>
                          <span className={styles.replacementPhrase}>"{replacement}"</span>
                          <button
                            type="button"
                            className={styles.removeReplacementButton}
                            onClick={() => handleRemovePhraseReplacement(original)}
                            title="Remove replacement"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add New Replacement */}
                <div className={styles.phraseReplacementGrid}>
                  <div className={styles.formGroup}>
                    <label htmlFor="phraseToReplace">Phrase to Replace</label>
                    <input
                      type="text"
                      id="phraseToReplace"
                      value={formData.phraseToReplace}
                      onChange={(e) => handleInputChange("phraseToReplace", e.target.value)}
                      placeholder="Enter phrase"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="replacementWord">Replacement Word</label>
                    <input
                      type="text"
                      id="replacementWord"
                      value={formData.replacementWord}
                      onChange={(e) => handleInputChange("replacementWord", e.target.value)}
                      placeholder="Enter replacement"
                    />
                  </div>
                  <button
                    type="button"
                    className={styles.addReplacementButton}
                    onClick={handleAddPhraseReplacement}
                    disabled={!formData.phraseToReplace.trim() || !formData.replacementWord.trim()}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Domain Selection */}
              <div className={styles.formGroup}>
                <label htmlFor="domain">Domain:</label>
                <select
                  id="domain"
                  value={formData.domain}
                  onChange={(e) => handleInputChange("domain", e.target.value as 'general' | 'medical' | 'legal' | 'technical' | 'islamic')}
                >
                  <option value="general">General</option>
                  <option value="medical">Medical</option>
                  <option value="legal">Legal</option>
                  <option value="technical">Technical</option>
                  <option value="islamic">Islamic</option>
                </select>
              </div>

              <div className={styles.testRoomCheckboxRow}>
                <input
                  type="checkbox"
                  id="isTestRoom"
                  checked={formData.isTest}
                  onChange={(e) => handleInputChange('isTest', e.target.checked)}
                  className={styles.testRoomCheckbox}
                />
                <label htmlFor="isTestRoom" className={styles.checkboxLabel}>
                  Mark as Test Room
                </label>
              </div>
              <div className={styles.saveSettingsButtonWrapper}>
                <button
                  className={styles.saveSettingsButton}
                  onClick={handleSaveSettings}
                  disabled={loading}
                >
                  {/* Removed img tag, rely on CSS for icons if needed or add accessible text */}
                  {loading ? "Saving..." : "Save Settings"}
                </button>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={onCancelClick}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedSetupScreen;
